import * as React from 'react';
import { Typography, Stack, List, ListItem, ListItemText } from '@mui/material';

export interface HelpContent {
    title?: string;
    content: React.ReactNode;
}

/**
 * Centralized help content dictionary for the application
 * Key: helpContext identifier
 * Value: HelpContent with title and MUI Stack component content
 */
export const HelpDictionary: Map<string, HelpContent> = new Map<string, HelpContent>();

// ============================================
// TABS & NAVIGATION
// ============================================

HelpDictionary.set('tab-search', {
    title: 'Search Tab',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Search through cutters worldwide using filters by serial number, company, country, software version, host names...
            </Typography>
            <Typography variant="body2">
                Enter at least 3 characters to search. Use spaces to combine criteria (e.g., &quot;Vector France&quot;).
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('tab-bookmarks', {
    title: 'Bookmarks Tab',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Your saved cutters for quick access and follow up.
            </Typography>
            <Typography variant="body2">
                Bookmarks are saved across sessions and synced locally.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('tab-stats', {
    title: 'Statistics Tab',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                View analytics on cutter connections, contracts, and equipment distribution worldwide.
            </Typography>
            <Typography variant="body2">
                Includes historical trends, regional analysis, and upgrade priorities.
            </Typography>
        </Stack>
    )
});

// ============================================
// SEARCH PAGE
// ============================================

HelpDictionary.set('search-input', {
    title: 'Search Input',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                <strong>Search by:</strong>
            </Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Serial Number (e.g., FC24JN6926)" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Company Name (e.g., Lectra)" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Country (e.g., France)" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Software Version (e.g., 8.0.4)" />
                </ListItem>
            </List>
            <Typography variant="body2">
                Minimum 3 characters required. Combine terms with spaces.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('search-refresh', {
    title: 'Refresh Results',
    content: (
        <Typography variant="body2">
            Refresh search results with the latest data from the database.
            Useful when monitoring real-time cutter status changes.
        </Typography>
    )
});

HelpDictionary.set('cutter-card', {
    title: 'Cutter Card',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Each card displays essential cutter information:
            </Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Serial Number & Machine Type" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Customer & Location Details" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Connection Status & Last Connection" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Software Versions (Pilot, Lisa, Firmware)" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Contract Status & Expiration" />
                </ListItem>
            </List>
        </Stack>
    )
});

HelpDictionary.set('bookmark-add', {
    title: 'Add Bookmark',
    content: (
        <Typography variant="body2">
            Add this cutter to your bookmarks for quick access and monitoring.
            Bookmarks are saved locally and persist across sessions.
        </Typography>
    )
});

HelpDictionary.set('expand-details', {
    title: 'Expand Details',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Expand to view detailed information:
            </Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Lisa Imports History" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Zip Reports" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Version History Timeline" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Hardware Error Logs" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Connection Timeline & Statistics" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Salesforce Cases" />
                </ListItem>
            </List>
        </Stack>
    )
});

// ============================================
// BOOKMARKS PAGE
// ============================================

HelpDictionary.set('bookmark-badge', {
    title: 'Bookmark Count',
    content: (
        <Typography variant="body2">
            Shows the number of bookmarked cutters. Click the tab to view all your saved cutters.
        </Typography>
    )
});

HelpDictionary.set('bookmark-remove', {
    title: 'Remove Bookmark',
    content: (
        <Typography variant="body2">
            Remove this cutter from your bookmarks. This action can be undone by re-bookmarking.
        </Typography>
    )
});

// ============================================
// STATISTICS PAGE
// ============================================

HelpDictionary.set('stats-conn-vs-contract', {
    title: 'Connections vs Contracts',
    content: (
        <Typography variant="body2">
            Historical trend showing cutter connectivity and contract coverage over time.
        </Typography>
    )
});

HelpDictionary.set('stats-upgrade-priority', {
    title: 'Equipment Upgrade Priority',
    content: (
        <Typography variant="body2">
            View equipment that may need software upgrades based on current versions and connection status.
        </Typography>
    )
});

HelpDictionary.set('stats-distribution', {
    title: 'Equipment Distribution Analysis',
    content: (
        <Typography variant="body2">
            View equipment distribution across regions, countries, and companies. Includes breakdown by business segments and product lines.
        </Typography>
    )
});

HelpDictionary.set('stats-sales-history', {
    title: 'Sales & Orders History',
    content: (
        <Typography variant="body2">
            View historical sales and equipment order data. Pre-computed statistics showing sales trends and distribution.
        </Typography>
    )
});

// ============================================
// CUTTER DETAILS
// ============================================

HelpDictionary.set('details-paperwork', {
    title: 'Administrative Information',
    content: (
        <Typography variant="body2">
            Salesforce cases, Contacts in factory and various current subscription and contract data.
        </Typography>
    )
});

HelpDictionary.set('details-contact', {
    title: 'Contact Information',
    content: (
        <Typography variant="body2">
            Customer contact information for this equipment.
        </Typography>
    )
});

HelpDictionary.set('details-contract', {
    title: 'Contract Details',
    content: (
        <Typography variant="body2">
            Service contract information including type, coverage dates, and terms.
        </Typography>
    )
});

HelpDictionary.set('details-connection', {
    title: 'Connection History',
    content: (
        <Typography variant="body2">
            Timeline showing when the equipment connected to the system and uploaded data.
        </Typography>
    )
});

HelpDictionary.set('details-errors', {
    title: 'Hardware Errors',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Hardware error log displaying:
            </Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Error Code & Category" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Occurrence Date & Time" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Error Description & Severity" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Error Source" />
                </ListItem>
            </List>
        </Stack>
    )
});

HelpDictionary.set('details-salesforce', {
    title: 'Salesforce Cases',
    content: (
        <Typography variant="body2">
            Support cases from Salesforce associated with this equipment.
        </Typography>
    )
});

HelpDictionary.set('details-factory', {
    title: 'Factory Configuration',
    content: (
        <Typography variant="body2">
            Original factory settings and hardware configuration including model specifications, 
            installed options, and manufacturing details.
        </Typography>
    )
});

// ============================================
// SYSTEM & UTILITIES
// ============================================

HelpDictionary.set('download-eventmanager', {
    title: 'Download EventManager',
    content: (
        <Typography variant="body2">
            Download the latest EventManager version.
        </Typography>
    )
});

HelpDictionary.set('refresh-data', {
    title: 'Data Update Status',
    content: (
        <Typography variant="body2">
            View when data was last updated from the ETL process.
        </Typography>
    )
});

HelpDictionary.set('theme-toggle', {
    title: 'Toggle Theme',
    content: (
        <Typography variant="body2">
            Switch between light and dark mode. Your preference is saved and applied across sessions.
        </Typography>
    )
});

HelpDictionary.set('export-excel', {
    title: 'Export to Excel',
    content: (
        <Typography variant="body2">
            Export current data to an Excel file for offline analysis.
        </Typography>
    )
});

HelpDictionary.set('filter-region', {
    title: 'Region Filter',
    content: (
        <Typography variant="body2">
            Filter data by geographic region.
        </Typography>
    )
});

HelpDictionary.set('filter-country', {
    title: 'Country Filter',
    content: (
        <Typography variant="body2">
            Filter by specific countries. Start typing to search.
        </Typography>
    )
});

HelpDictionary.set('filter-company', {
    title: 'Company Filter',
    content: (
        <Typography variant="body2">
            Filter by company name. Use autocomplete to search and select.
        </Typography>
    )
});

HelpDictionary.set('timeline-slider', {
    title: 'Timeline',
    content: (
        <Typography variant="body2">
            Adjust the time range to filter data chronologically.
        </Typography>
    )
});

HelpDictionary.set('cutter-timeline', {
    title: 'Cutter Timeline',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Visual history of all key events for this equipment over the last 6 months.
                Bubble size reflects how many events of the same category occurred in the same time unit.
            </Typography>
            <Typography variant="body2"><strong>Positioning</strong></Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Drag the title bar to move the panel anywhere on screen" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Position is remembered — the panel reopens at its last location" />
                </ListItem>
            </List>
            <Typography variant="body2"><strong>Navigation &amp; Zoom</strong></Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Mouse scroll — zoom in / out centred on the cursor" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Click &amp; drag on the chart area — select a custom date range to zoom in" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Reset zoom button — return to the full 6-month view" />
                </ListItem>
            </List>
            <Typography variant="body2">
                Click any bubble to navigate directly to the relevant detail tab.
                Zip report bubbles also trigger a file download.
            </Typography>
        </Stack>
    )
});

// ============================================
// DETAIL VIEW - CONNECTION TAB
// ============================================

HelpDictionary.set('details-zip-uploads', {
    title: 'Zip Uploads',
    content: (
        <Typography variant="body2">
            EventManager zip file uploads from the equipment. Each file contains connection reports and system logs. Upload frequency indicates connection health.
        </Typography>
    )
});

HelpDictionary.set('details-lisa-imports', {
    title: 'Lisa Imports',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Timeline of data imports into Lisa database showing:
            </Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Extract date and last report date" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Days since last report" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Year/Week reference" />
                </ListItem>
            </List>
        </Stack>
    )
});

HelpDictionary.set('details-dashboard', {
    title: 'Dashboard Data',
    content: (
        <Typography variant="body2">
            Historical dashboard events and metrics for this equipment.
        </Typography>
    )
});

// ============================================
// DETAIL VIEW - SYSTEM TAB
// ============================================

HelpDictionary.set('details-software-updates', {
    title: 'Software Updates',
    content: (
        <Typography variant="body2">
            Software version history showing updates to cutting software and system components over time.
            <ul>
                <li><strong style={{ color: '#dc3545' }}>red </strong> means a rollback to an older version</li>
                <li><strong style={{ color: '#28a745' }}>green </strong> indicates an update to a newer version</li>
            </ul>
        </Typography>
    )
});

HelpDictionary.set('details-versalis-updates', {
    title: 'Versalis Software',
    content: (
        <Typography variant="body2">
            Versalis-specific software version history. Only displayed for Versalis model equipment.
        </Typography>
    )
});

HelpDictionary.set('details-hardware-changes', {
    title: 'Hardware History',
    content: (
        <Typography variant="body2">
            Historical hardware configuration data showing equipment changes and modifications over time.
        </Typography>
    )
});

HelpDictionary.set('details-errors', {
    title: 'Hardware Errors',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                System-reported hardware errors showing:
            </Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Error code, category, and description" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Occurrence timestamps" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Error source and severity" />
                </ListItem>
            </List>
            <Typography variant="body2">
                Error data is provided <b>over Dashboard connection</b>. The date range represents the complete error history available (up to 3 months from the last available error).
            </Typography>
            <Typography variant="body2">
                Use the expand button to enlarge this panel. Hover over column headers and use the <strong>⋮ icon</strong> for sorting options.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('details-counters', {
    title: 'Machine Counters',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Cumulative machine counters extracted from the <strong>zip reports</strong> uploaded by the equipment.
                Each row corresponds to one zip file; values represent the counters state at the moment the zip was
                created on the machine (<strong>zip creation date</strong> is the reference timestamp, not the upload date).
            </Typography>
            <Typography variant="body2">
                Because counters are <em>cumulative</em>, successive rows show steadily increasing values — the
                delta between two rows represents activity over that interval.
                Not every counter is present in every report: older firmware versions may expose fewer metrics.
            </Typography>
            <Typography variant="body2"><strong>Filters</strong></Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Counters selector — pick which counters to display; empty = none, all checked = show all. Use Select all / Select none at the top of the list." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Date range slider — drag handles to zoom in on a time window; left/right arrows step day by day; calendar icon jumps to a specific day." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Brush — drag directly on the chart to zoom into a sub-range; the slider updates accordingly." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Reset filters — restores the counter selection to all counters and the date range to the full history." />
                </ListItem>
            </List>
            <Typography variant="body2"><strong>Presets</strong></Typography>
            <List dense sx={{ py: 0 }}>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Click a preset chip to apply its saved counter selection instantly." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• ✎ icon inside the chip — rename the preset inline (Enter to confirm, Esc to cancel)." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• + button — creates a new preset pre-filled with the current selection (hidden when 5 presets are stored)." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• 💾 Save — overwrites the focused preset with the current selection (enabled only when the selection differs)." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• 🗑 Remove — deletes the focused preset slot." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Export / Import — save all presets to a JSON file or restore them on another browser or machine." />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="• Drag chips left or right to reorder them; changes are persisted automatically." />
                </ListItem>
            </List>
            <Typography variant="body2">
                Use the <strong>expand button</strong> in the tab to open the panel in a larger detached view.
                Filters and the selected view mode are shared between the inline and detached panels.
            </Typography>
        </Stack>
    )
});

// ============================================
// DETAIL VIEW - PAPERWORK TAB
// ============================================

HelpDictionary.set('details-salesforce-cases', {
    title: 'Salesforce Cases',
    content: (
        <Typography variant="body2">
            Support cases from Salesforce linked to this equipment, including case details and status information.
        </Typography>
    )
});

HelpDictionary.set('details-contacts', {
    title: 'Contacts',
    content: (
        <Typography variant="body2">
            Customer contact information from Salesforce for this equipment.
        </Typography>
    )
});

HelpDictionary.set('details-subscriptions', {
    title: 'Contract Information',
    content: (
        <Typography variant="body2">
            Service contract details including contract type, coverage period, and terms.
        </Typography>
    )
});

HelpDictionary.set('details-maintenance', {
    title: 'Maintenance',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Maintenance data extracted from ZIP reports uploaded by the cutter.
            </Typography>
            <Typography variant="body2">
                <strong>Maintenance Acts</strong> — intervention history from <em>CMachine/ServiceHistory.xml</em> (cumulative across all ZIPs).
            </Typography>
            <Typography variant="body2">
                <strong>Maintenance Plan</strong> — scheduled service state from <em>CMachine/Services.xml</em> (point-in-time snapshot per ZIP).
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('details-maintenance-contract', {
    title: 'Maintenance Contract',
    content: (
        <Typography variant="body2">
            Contract details including warranty coverage, maintenance terms, and service information.
        </Typography>
    )
});

HelpDictionary.set('details-maintenance-acts', {
    title: 'Maintenance Acts',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The last 30 maintenance interventions performed on this cutter, ordered by most recent first.
            </Typography>
            <Typography variant="body2">
                Each row represents a service act extracted from <strong>CMachine/ServiceHistory.xml</strong> inside the ZIP report: act name, type, duration, counter value at the time of the intervention, and technician notes.
            </Typography>
            <Typography variant="body2">
                This file is <em>cumulative</em> — every ZIP snapshot contains the full history since installation. The most recent ZIP always wins on duplicate entries.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('details-maintenance-plan', {
    title: 'Maintenance Plan',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The current scheduled maintenance state for this cutter, one row per service item.
            </Typography>
            <Typography variant="body2">
                Data is extracted from <strong>CMachine/Services.xml</strong> inside each ZIP report. This file is a <em>point-in-time snapshot</em> — values change with each new ZIP upload and reflect the machine state at upload time.
            </Typography>
            <Typography variant="body2">
                <strong>Overdue</strong> (red) services have exceeded their maintenance threshold. <strong>Warning</strong> (orange) services expire within 30 days.
            </Typography>
        </Stack>
    )
});

// ============================================
// ERP CONTRACT TAB
// ============================================

HelpDictionary.set('erp-contract-period', {
    title: 'Contract Period',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The start and end dates of the active maintenance contract registered in the ERP system.
            </Typography>
            <Typography variant="body2">
                The contract covers a fixed calendar period during which the agreed cutting hours are consumed.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('erp-contract-contractual-time', {
    title: 'Contractual Cutting Time',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The total number of cutting hours signed in the maintenance contract.
            </Typography>
            <Typography variant="body2">
                This is the reference quota against which consumption is measured. Once this threshold is reached, the contract is fully consumed.
            </Typography>
            <Typography variant="body2">
                A value of <strong>0</strong> means the contract has <em>unlimited</em> cutting time — no quota is enforced and consumption metrics are not applicable.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('erp-contract-additional-time', {
    title: 'Additional Granted Time',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                Extra cutting hours granted on top of the signed contractual time — for example, as commercial goodwill or a contract amendment.
            </Typography>
            <Typography variant="body2">
                This value does not affect the usage percentage or remaining time calculations, which are based solely on the contractual cutting time.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('erp-contract-reference-time', {
    title: 'Reference Cutting Time',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The effective cutting time recorded on the cutter <strong>at the moment the maintenance contract started</strong>.
            </Typography>
            <Typography variant="body2">
                It is captured from either the effective cutting time counter or the vacuum time counter — both are labelled <em>Effective Cutting Time</em> on the equipment.
            </Typography>
            <Typography variant="body2">
                This value serves as the baseline: all subsequent consumption is measured relative to it.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('erp-contract-last-effective', {
    title: 'Last Effective Cutting Time',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The most recent effective cutting time value reported by the cutter, sourced from the ERP Oracle system.
            </Typography>
            <Typography variant="body2">
                This is either the effective cutting time counter or the vacuum time counter, depending on the cutter model. The difference with the <strong>Reference Cutting Time</strong> gives the actual hours worked since the contract started.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('erp-contract-last-update', {
    title: 'Last Update Date',
    content: (
        <Typography variant="body2">
            The date when the Last Effective Cutting Time value was last reported and recorded in the ERP system.
        </Typography>
    )
});

HelpDictionary.set('erp-contract-worked-hours', {
    title: 'Hours Worked Since Contract Start',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The number of cutting hours the cutter has accumulated <strong>since the contract started</strong>.
            </Typography>
            <Typography variant="body2">
                Calculated as: <strong>Last Effective Cutting Time − Reference Cutting Time</strong>.
            </Typography>
            <Typography variant="body2">
                This is the consumption figure used to compute both the usage percentage and the remaining time.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('erp-contract-remaining-time', {
    title: 'Remaining Time',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The number of cutting hours still available before the contractual quota is fully consumed.
            </Typography>
            <Typography variant="body2">
                Calculated as: <strong>Contractual Cutting Time − Hours Worked Since Contract Start</strong>.
            </Typography>
            <Typography variant="body2">
                Displayed in <em style={{ color: 'orange' }}>orange</em> when below 10% of the contractual time, and in <em style={{ color: 'red' }}>red</em> when the limit has been exceeded.
            </Typography>
            <Typography variant="body2">
                Shown as the <em>number of days until the contract end date</em> when the contractual cutting time is 0 (unlimited), since no hour quota is enforced. Turns <em style={{ color: 'orange' }}>orange</em> when fewer than 30 days remain, and <em style={{ color: 'red' }}>red</em> if the contract has already expired.
            </Typography>
        </Stack>
    )
});

HelpDictionary.set('erp-contract-usage-pct', {
    title: 'Usage Percentage',
    content: (
        <Stack spacing={1}>
            <Typography variant="body2">
                The proportion of the signed contractual cutting time that has been consumed.
            </Typography>
            <Typography variant="body2">
                Calculated as: <strong>(Hours Worked Since Contract Start ÷ Contractual Cutting Time) × 100</strong>.
            </Typography>
            <Typography variant="body2">
                Color coding: <em style={{ color: 'green' }}>green</em> below 90% · <em style={{ color: 'orange' }}>orange</em> from 90% · <em style={{ color: 'red' }}>red</em> at or above 100%.
            </Typography>
            <Typography variant="body2">
                Shown as <em>N/A – Unlimited contract</em> when the contractual cutting time is 0, as division by zero is not applicable.
            </Typography>
        </Stack>
    )
});

// ============================================
// DETAILS VIEW FACTORY
// ============================================

HelpDictionary.set('details-factory', {
    title: 'Factory Information',
    content: (
        <Typography variant="body2">
            Equipment manufacturing details including models, configuration, and initial installation information.
        </Typography>
    )
});

export default HelpDictionary;
